import React, {FocusEvent, MouseEvent, useEffect, useRef} from "react";
import {observer} from "mobx-react";
import {IFormField} from "../../../models/forms/form-field.interface";
import {CardExpiration} from "../../../types/card-expiration";
import { useMask, MaskOptions, Replacement} from '@react-input/mask';
import {InputBaseBox} from "../../../basic-components/input/input-base.box";
import {useServices} from "../../../hooks/use-services.hook";
import {CardExpirationParser} from "./card-expiration-parser";
import {FieldEditorCommonProps, FieldInputContainerComponentProps} from "../inputs-common.props";
import {FieldInputContainerComponent} from "../field-input-container.component";


const digitRegEx = /\d/

const CardExpirationMaskReplacement: Replacement = {
    M: digitRegEx,
    Y: digitRegEx,
};

const CardExpirationMask = "MM/YY";



interface CardExpirationFieldEditorComponentProps extends FieldEditorCommonProps {
    field: IFormField<CardExpiration | null>;
}

export const CardExpirationFieldEditorComponent: React.FC<CardExpirationFieldEditorComponentProps> = observer((props) => {
    const services = useServices();
    const ignoreFieldChangeEventRef = useRef(false);
    const [currentInputValue, setCurrentInputValue] = React.useState<string>("");

    const maskOptions: MaskOptions = {
        mask: CardExpirationMask,
        replacement: CardExpirationMaskReplacement,
        showMask: false,
        separate: true
    };

    const inputRef = useMask(maskOptions);
    const valueParser = useRef(new CardExpirationParser(services, {
        mask: CardExpirationMask,
        replacement: CardExpirationMaskReplacement
    }));

    useEffect(() => {
        const subscription = props.field.onChange((newValue) => {
            if(!ignoreFieldChangeEventRef.current) {
                if (newValue) {
                    inputRef.current.value = newValue.toString();
                } else {
                    inputRef.current.value = "";
                }
            }
        });

        if(inputRef.current) {
            props.field.attachInputDOMElement(inputRef.current);
        }


        return () => {
            subscription.unsubscribe();
        }
    }, [inputRef, props.field]);

    const setFieldValue = (inputValue: string, setInputValueAlso: boolean) => {
        ignoreFieldChangeEventRef.current = true;
        try {
            const parseResult = valueParser.current.parseValue(inputValue);
            props.field.setCustomError(parseResult.error);
            props.field.setValue(parseResult.value);
            if(setInputValueAlso) {
                inputRef.current.value = parseResult.formattedValue ?? "";
            }
        } catch(err) {
            services.logger.error('Failed to set card expiration field value', err);
        } finally {
            ignoreFieldChangeEventRef.current = false;
        }
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(event.target.value, false);
        setCurrentInputValue(event.target.value);
    }

    const onBlur = (event: FocusEvent<HTMLInputElement>)=> {
        setFieldValue(event.target.value, true);
    }


    const renderInput = () => {
        const defaultValue = props.field.value?.toString() ?? "";

        return (
            <InputBaseBox ref={inputRef}
                          placeholder={CardExpirationMask}
                          defaultValue={defaultValue}
                          onChange={onChange}
                          onBlur={onBlur}/>
        )
    }

    const onClearButtonClick = (event: MouseEvent<HTMLDivElement>)=> {
        if(props.onClearButtonClick) {
            props.onClearButtonClick(event);
        }

        if(event.isDefaultPrevented() || event.isPropagationStopped()) {
            return;
        }

        props.field.clear();
        inputRef.current.value = "";
        setCurrentInputValue("");
    }


    const forceClearButton = props.showClearButton && Boolean(currentInputValue || props.field.value);

    const fieldContainerProps: FieldInputContainerComponentProps = {
        ...props,
        renderInput: renderInput,
        onClearButtonClick: onClearButtonClick,
        forceClearButton: forceClearButton,
        showClearButton: forceClearButton
    }


    return (
        <FieldInputContainerComponent {...fieldContainerProps}/>
    );


})